/**
 * Storage Manager for ConEx360
 *
 * Manages two independent storage objects:
 * 1. SessionObject - Stores session-related metadata (cleared on logout/tab close)
 * 2. Conex360Object - Stores user and navigation tracking (persists across sessions)
 */

import { AppLogger } from '@/lib/logger'
const log = AppLogger.getLogger('lib/storageManager')

/**
 * Session Object Schema
 * Stored in: sessionStorage['sessionInfo']
 * Cleared on: logout, tab close
 */
export interface SessionObject {
  /** Whether the session is currently valid (both factors passed) */
  isValid: boolean

  /** Timestamp (milliseconds) of the last successful verification */
  lastActivity: number

  /** Timestamp (milliseconds) when the session expires (from token exp claim) */
  expiresAt: number

  /** Seconds remaining until token expiration */
  tokenExpiresIn: number

  /** Timestamp (milliseconds) when the token was last refreshed */
  refreshedAt: number
}

/**
 * ConEx360 Object Schema
 * Stored in: localStorage['conex360']
 * Persists across sessions and browser tabs
 * Used for user tracking and navigation analytics
 */
export interface Conex360Object {
  /** Username of the currently logged-in user */
  username: string

  /** The last route the user visited */
  lastVisitedRoute: string

  /** Unix timestamp (milliseconds) of the last route change */
  lastVisitedTime: number

  /** Additional extensible attributes for future use */
  [key: string]: string | number | boolean | object | undefined
}

// Storage keys
const SESSION_STORAGE_KEY = 'sessionInfo'
const LOCAL_STORAGE_KEY = 'conex360'

/**
 * SessionStorage Manager
 * Handles session-specific data that expires with the session
 */
export const SessionStorageManager = {
  /**
   * Get the session object from sessionStorage
   */
  getSession(): SessionObject | null {
    try {
      const item = sessionStorage.getItem(SESSION_STORAGE_KEY)
      return item ? JSON.parse(item) : null
    } catch (error) {
      log.warn('Failed to parse session object from storage', error)
      return null
    }
  },

  /**
   * Set the session object in sessionStorage
   */
  setSession(session: SessionObject): void {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
    } catch (error) {
      log.error('Failed to store session object', error)
    }
  },

  /**
   * Update specific fields in the session object
   */
  updateSession(updates: Partial<SessionObject>): void {
    const current = this.getSession()
    if (current) {
      this.setSession({ ...current, ...updates })
    }
  },

  /**
   * Clear the session object from sessionStorage
   */
  clearSession(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  },

  /**
   * Check if session is valid
   */
  isSessionValid(): boolean {
    const session = this.getSession()
    return session?.isValid ?? false
  },
}

/**
 * LocalStorage Manager for ConEx360 Object
 * Handles user and navigation tracking across sessions
 */
export const Conex360StorageManager = {
  /**
   * Get the ConEx360 object from localStorage
   */
  get(): Conex360Object | null {
    try {
      const item = localStorage.getItem(LOCAL_STORAGE_KEY)
      return item ? JSON.parse(item) : null
    } catch (error) {
      log.warn('Failed to parse conex360 object from storage', error)
      return null
    }
  },

  /**
   * Set the ConEx360 object in localStorage
   */
  set(data: Conex360Object): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      log.error('Failed to store conex360 object', error)
    }
  },

  /**
   * Update specific fields in the ConEx360 object
   * Creates a new object if it doesn't exist
   */
  update(updates: Partial<Conex360Object>): void {
    const current = this.get() || {
      username: '',
      lastVisitedRoute: '',
      lastVisitedTime: 0,
    }
    this.set({ ...current, ...updates } as Conex360Object)
  },

  /**
   * Update navigation tracking on route change
   * @param route - The new route path
   * @param username - The username of the current user
   */
  updateRouteNavigation(route: string, username?: string): void {
    const now = Date.now()
    const updates: Partial<Conex360Object> = {
      lastVisitedRoute: route,
      lastVisitedTime: now,
    }
    if (username) {
      updates.username = username
    }
    this.update(updates)
  },

  /**
   * Get the last visited route with timestamp
   */
  getLastVisited(): { route: string; time: number } | null {
    const data = this.get()
    if (data) {
      return {
        route: data.lastVisitedRoute,
        time: data.lastVisitedTime,
      }
    }
    return null
  },

  /**
   * Get username from ConEx360 storage
   */
  getUsername(): string | null {
    const data = this.get()
    return data?.username ?? null
  },

  /**
   * Set username in ConEx360 storage
   */
  setUsername(username: string): void {
    this.update({ username })
  },

  /**
   * Add custom attribute to ConEx360 object
   * Useful for storing additional user or app data
   */
  setAttribute(key: string, value: string | number | boolean | object): void {
    this.update({ [key]: value })
  },

  /**
   * Get custom attribute from ConEx360 object
   */
  getAttribute(key: string): string | number | boolean | object | undefined {
    const data = this.get()
    return data ? data[key] : undefined
  },

  /**
   * Clear the ConEx360 object from localStorage
   * Called on logout
   */
  clear(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  },
}

/**
 * Combined Storage Manager for convenience
 */
export const StorageManager = {
  session: SessionStorageManager,
  conex360: Conex360StorageManager,
}

/**
 * Initialize ConEx360 storage with user information
 * Call this after successful login
 */
export function initializeConex360Storage(username: string, route: string = '/'): void {
  Conex360StorageManager.set({
    username,
    lastVisitedRoute: route,
    lastVisitedTime: Date.now(),
  })
}
